// F8 / FR-RD15: while a Zoom/Teams window is being recorded, watch for that window closing.
// When it goes, a 10 s countdown runs (shown in the HUD) and then recording stops — unless
// the user chooses "Keep recording". Polling uses the same `meeting_windows` command as the
// detector; a failed poll counts as "still open" so a hiccup can never end a recording.
import { useCallback, useEffect, useRef, useState } from "react";
import { MEETING_POLL_MS, createWindowCloseWatcher, listMeetingWindows } from "./meetingCapture";

export function useMeetingAutoStop(opts: { active: boolean; sourceId: string | null; onStop: () => void }) {
  const [closingIn, setClosingIn] = useState<number | null>(null);
  const onStopRef = useRef(opts.onStop);
  onStopRef.current = opts.onStop;
  const watcherRef = useRef<ReturnType<typeof createWindowCloseWatcher> | null>(null);
  const keptRef = useRef(false);

  useEffect(() => {
    if (!opts.active || !opts.sourceId) return;
    keptRef.current = false;
    const watcher = createWindowCloseWatcher({ onTick: setClosingIn, onStop: () => onStopRef.current() });
    watcherRef.current = watcher;
    let cancelled = false;
    async function poll() {
      let open = true;
      try {
        open = (await listMeetingWindows()).some((w) => w.source_id === opts.sourceId);
      } catch {
        /* keep recording */
      }
      if (!cancelled && !keptRef.current) watcher.update(open);
    }
    const id = setInterval(() => void poll(), MEETING_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
      watcher.cancel();
      watcherRef.current = null;
      setClosingIn(null);
    };
  }, [opts.active, opts.sourceId]);

  /** "Keep recording": cancels a running countdown and stops watching this window. */
  const keep = useCallback(() => {
    keptRef.current = true;
    watcherRef.current?.cancel();
    setClosingIn(null);
  }, []);

  return { closingIn, keep };
}
